const GRAPHQL_ENDPOINT = '/api/landings/magento?path=/graphql';

export async function executeQuery(query: string, variables = {}) {
	const response = await fetch(GRAPHQL_ENDPOINT, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		credentials: 'include',
		body: JSON.stringify({
			query,
			variables
		})
	});

	if (!response.ok) {
		throw new Error(`Network error: ${response.status}`);
	}

	const result = await response.json();
	
	if (result.errors?.length) {
		const messages = result.errors.map((err: any) => err.message).join('\n');
		throw new Error(messages);
	}

	return result?.data ?? null;
}

export async function createEmptyCart() {
	const query = `
		mutation {
			createEmptyCart
		}
	`;

	const result = await executeQuery(query);
	return result?.createEmptyCart ?? null;
}

export type MagentoProduct = {
	sku: string;
	name: string;
	mindbox_id?: string | null;
	special_price?: number | null;
	price?: {
		maximum_price?: {
			final_price?: {
				value?: number | null;
			};
		};
	};
	stock_status: string;
	image?: {
		url: string;
	};
	custom_attributesV2?: {
		items?: Array<{ code?: string; value?: string }>;
	};
};

export async function getProducts(skus: string[]): Promise<MagentoProduct[]> {
	const query = `
		query GetProducts($skus: [String!]!) {
			products(filter: { sku: { in: $skus } }, pageSize: 30) {
				items {
					sku
					name
					mindbox_id
					special_price
					price: price_range {
						maximum_price {
							final_price {
								value
								currency
							}
						}
					}
					stock_status
					image {
						url
					}
					__typename
					... on ConfigurableProduct {
						variants {
							product {
								sku
								name
								mindbox_id
								special_price
								price: price_range {
									maximum_price {
										final_price { value currency }
									}
								}
								image { url }
								stock_status
							}
						}
					}
				}
			}
		}
	`;

	const result = await executeQuery(query, { skus });
	const items = (result?.products?.items || []) as Array<any>;
	const flattened: Array<any> = [];

	for (const item of items) {
		flattened.push(item);
		if (item?.variants?.length) {
			for (const variant of item.variants) {
				if (variant?.product) {
					flattened.push(variant.product);
				}
			}
		}
	}

	const requestedSkuSet = new Set(skus);
	const exactMatches = flattened.filter(p => requestedSkuSet.has(p.sku));
	const orderMap: Record<string, number> = skus.reduce((acc, sku, index) => {
		acc[sku] = index;
		return acc;
	}, {} as Record<string, number>);

	exactMatches.sort((a, b) => orderMap[a.sku] - orderMap[b.sku]);
	return exactMatches as MagentoProduct[];
}

export async function addProductToCart(cartId: string, sku: string, quantity = 1) {
	const query = `
		mutation AddProductToCart($cartId: String!, $sku: String!, $quantity: Float!) {
			addSimpleProductsToCart(
				input: {
					cart_id: $cartId,
					cart_items: [
						{
							data: {
								quantity: $quantity,
								sku: $sku
							}
						}
					]
				}
			) {
				cart {
					items {
						product {
							sku
							name
							image {
								url
							}
							price_range {
								maximum_price {
									regular_price {
										value
										currency
									}
									final_price {
										value
										currency
									}
									discount {
										amount_off
										percent_off
									}
								}
							}
						}
						quantity
					}
				}
			}
		}
	`;

	const variables = {
		cartId,
		sku,
		quantity
	};

	return executeQuery(query, variables);
}

type ProductInfo = {
	mindbox_id: string;
	price: number;
};

type MindboxProductListInput = {
	identifier: string;
	email: string;
	is_subscribed?: boolean;
	product_list: ProductInfo[];
	client_main_problem?: string[];
};

export async function setMindboxProductList(
	input: MindboxProductListInput
) {
	const query = `
    mutation setMindboxProductList($input: MindboxProductListInput!) {
      setMindboxProductList(input: $input)
    }
  `;

	return executeQuery(query, { input });
}
